import { useQuery } from '@apollo/client';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TablePagination,
    Typography,
    useTheme
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import moment from 'moment';
import * as React from 'react';
import { InterviewKind } from 'shared/models/interview';
import { getDisqualReasonLabel } from 'shared/models/job-stages';

import { getCandidateKeysWhere, PipelineView, PipelineWhere } from '../common/recruiter-pipeline';
import { callTime } from '../common/timestamp';
import {
    PIPELINE_CANDIDATES,
    PIPELINE_INTERVIEWS,
    PipelineCandidate,
    PipelineCandidatesData,
    PipelineCandidatesVariables,
    PipelineInterview
} from '../graphql/queries/recruiter-pipeline';
import { useSlides } from '../hooks/use-candidate-slides';
import { useReduxState } from '../hooks/use-redux';
import { CellValue, DataPanel, DataPanelColumn, dataPanelDialogStyles } from './data-panel';

export interface PipelineSelection {
    title: string;
    where: PipelineWhere;
    view: PipelineView;
    now: number;
    interviewKind?: InterviewKind;
}

const pageSize = 25;
type Column =
    | 'candidate'
    | 'client'
    | 'job'
    | 'recruiter'
    | 'stage'
    | 'interviewDate'
    | 'timeInStage'
    | 'dateLost'
    | 'reason';

export const RecruiterPipelineCandidates: React.FC<{ selection: PipelineSelection; onClose: () => void }> = ({
    selection,
    onClose
}) => {
    const theme = useTheme();
    const jobStages = useReduxState((state) => state.appConstants.constants.jobStages);
    const [page, setPage] = React.useState(0);
    const { setList } = useSlides();
    const lost = selection.view === 'lost';
    const showInterview = !lost && !!selection.interviewKind;
    const { data, loading, error, refetch } = useQuery<PipelineCandidatesData, PipelineCandidatesVariables>(
        PIPELINE_CANDIDATES,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            variables: {
                limit: pageSize,
                offset: page * pageSize,
                orderBy: [
                    lost ? { disqualifiedAt: 'desc' } : { lastStageChangedAt: 'desc' },
                    { personId: 'asc' },
                    { jobId: 'asc' }
                ],
                where: selection.where
            }
        }
    );
    const {
        data: interviewData,
        loading: interviewsLoading,
        error: interviewsError
    } = useQuery<{
        interviews: PipelineInterview[];
    }>(PIPELINE_INTERVIEWS, {
        fetchPolicy: 'network-only',
        skip: !showInterview || loading || !data?.candidates.length,
        variables: {
            where: {
                ...getCandidateKeysWhere(data?.candidates ?? []),
                kind: { _eq: selection.interviewKind }
            }
        }
    });

    const handleChangePage = (_: unknown, nextPage: number) => setPage(nextPage);
    const handleRetry = () => refetch({ offset: page * pageSize });
    const handleCandidateClick = (candidate: PipelineCandidate) => () => {
        setList(data.candidates, candidate);
    };

    const detailColumns: Array<DataPanelColumn<Column>> = lost
        ? [
              { id: 'dateLost', label: 'Disqualified Date' },
              { id: 'reason', label: 'Reason' }
          ]
        : [{ id: 'timeInStage', label: 'Time in Stage' }];
    const interviewColumns: Array<DataPanelColumn<Column>> = showInterview
        ? [{ id: 'interviewDate', label: `${selection.interviewKind} Date` }]
        : [];
    const columns: Array<DataPanelColumn<Column>> = [
        { id: 'candidate', label: 'Candidate' },
        { id: 'client', label: 'Client' },
        { id: 'job', label: 'Job' },
        { id: 'recruiter', label: 'Recruiter' },
        { id: 'stage', label: 'Stage' },
        ...interviewColumns,
        ...detailColumns
    ];
    const getInterviewDate = (candidate: PipelineCandidate) => {
        if (interviewsLoading) return 'Loading…';
        if (interviewsError) return 'Unavailable';
        const interview = interviewData?.interviews.find(
            (i) => i.personId === candidate.personId && i.jobId === candidate.jobId
        );
        return interview ? callTime(interview.startTime) : '—';
    };
    const getCellValue =
        (column: Column) =>
        (candidate: PipelineCandidate): CellValue => {
            switch (column) {
                case 'candidate':
                    return candidate.person?.name || 'Unknown candidate';
                case 'client':
                    return candidate.job.client.name;
                case 'job':
                    return candidate.job.title;
                case 'recruiter':
                    return candidate.recruiter.name;
                case 'stage':
                    return jobStages.find((stage) => stage.name === candidate.stage)?.label ?? candidate.stage;
                case 'interviewDate':
                    return getInterviewDate(candidate);
                case 'timeInStage':
                    return moment(candidate.lastStageChangedAt).from(selection.now, true);
                case 'dateLost':
                    return moment(candidate.disqualifiedAt).format('MMM DD, YYYY');
                case 'reason':
                    return candidate.disqualReason
                        ? getDisqualReasonLabel(candidate.disqualReason, true)
                        : 'Not recorded';
            }
        };
    const getCellRenderer = {
        candidate: (value: CellValue, candidate: PipelineCandidate) => (
            <span className="clickable-cell" onClick={handleCandidateClick(candidate)}>
                {value}
            </span>
        )
    };

    const retry = <Button onClick={handleRetry}>Retry</Button>;
    const errorMessage = error ? (
        <Alert severity="error" action={retry}>
            Unable to load candidates.
        </Alert>
    ) : null;
    const emptyMessage =
        !loading && !error && data?.candidates.length === 0 ? (
            <Typography>No candidates match this selection.</Typography>
        ) : null;
    const table = error ? null : (
        <DataPanel
            columns={columns}
            data={loading ? undefined : data?.candidates}
            getCellValue={getCellValue}
            getCellRenderer={getCellRenderer}
            hidePagination={true}
            pageSize={pageSize}
        />
    );
    const content = (
        <DialogContent>
            {errorMessage}
            {emptyMessage}
            <div className="data-panel">{table}</div>
        </DialogContent>
    );
    const pagination =
        data && !error ? (
            <TablePagination
                component="div"
                count={data.total.aggregate.count}
                rowsPerPage={pageSize}
                rowsPerPageOptions={[pageSize]}
                page={page}
                onChangePage={handleChangePage}
                nextIconButtonProps={{ disabled: loading || (page + 1) * pageSize >= data.total.aggregate.count }}
                backIconButtonProps={{ disabled: loading || page === 0 }}
            />
        ) : null;
    const actions = (
        <DialogActions>
            {pagination}
            <Button onClick={onClose}>Close</Button>
        </DialogActions>
    );
    return (
        <Dialog open={true} onClose={onClose} maxWidth="lg" fullWidth={true} css={dataPanelDialogStyles(theme)}>
            <DialogTitle>{selection.title}</DialogTitle>
            {content}
            {actions}
        </Dialog>
    );
};
